<template>
  <div class="col-12">
    <p class="mb-2">{{ $t("backend.candidacy.forms.select.selectable_options") }}</p>

    <TransitionGroup name="list" tag="ol" class="list-unstyled vstack gap-2">
      <li
        v-for="(option, index) in currentValue"
        :key="`selectable_option_${option.identifier}`"
        class="card rounded flex-row bg-light"
      >
        <div class="h-auto">
          <PositionControls
            :position="index + 1"
            :total-items="currentValue.length"
            @move-up="moveUp(index)"
            @move-down="moveDown(index)"
          />
        </div>
        <LocaleStringField
          :initial-value="option.label"
          :scope="[...scope, '', 'label']"
          :custom-id="`option_item_${option.identifier}`"
          :locales="enabledLocales"
          :placeholder="$t('backend.candidacy.forms.select.placeholder', { position: index + 1 })"
          :errors="errors"
          class="m-3 w-100"
          hide-label
          @update="updateOption($event, option.identifier)"
        />
        <button
          class="btn btn-delete-option rounded-start-0"
          :aria-label="$t('backend.candidacy.forms.select.delete_option', { position: index + 1 })"
          @click.prevent="deleteOption(index)"
        >
          <AVIcon icon="trash" />
        </button>
        <input :name="scopeToName([...scope, '', 'identifier'])" :value="option.identifier" type="hidden" />
      </li>
      <button
        key="new_option_btn"
        class="btn btn-new-option card rounded text-dark hstack gap-1 justify-content-center"
        @click.prevent="newOption"
      >
        <AVIcon icon="plus" />
        <p class="m-0">{{ $t("backend.candidacy.forms.select.new_option") }}</p>
      </button>
    </TransitionGroup>
  </div>

  <template v-if="!!deletedOptions.length">
    <input
      v-for="optionReference in deletedOptions"
      :key="`deleted_${optionReference}`"
      :name="scopeToName([...scope, ''])"
      :value="optionReference"
      type="hidden"
    />
  </template>
</template>

<script setup lang="ts">
import type { BuildableItemError, LocalString, Locale, PropType, SelectableOptionItem } from "@/types";
import { watch, reactive, onMounted } from "vue";
import LocaleStringField from "./LocaleStringField.vue";
import PositionControls from "@/components/backend/candidacy/shared/PositionControls.vue";
import { scopeToName } from "@/lib/candidacy/form_builder/utils";

const emits = defineEmits(["update:modelValue"]);

const props = defineProps({
  scope: {
    type: Array as PropType<string[]>,
    required: true,
  },
  errors: {
    type: Array as PropType<BuildableItemError[]>,
    default: [],
  },
  enabledLocales: {
    type: Array as PropType<Locale[]>,
    required: true,
  },
  modelValue: {
    type: Object as PropType<SelectableOptionItem[]>,
    required: true,
  },
});

const currentValue = reactive<SelectableOptionItem[]>([]);

const deletedOptions = reactive<string[]>([]);

const moveUp = (index: number) => {
  if (index <= 0) return;
  
  const firstIndex = index - 1;
  const lastIndex = index;
  currentValue.splice(firstIndex, 2, currentValue[lastIndex], currentValue[firstIndex]);
};

const moveDown = (index: number) => {
  if (index + 1 >= currentValue.length) return;

  const firstIndex = index;
  const lastIndex = index + 1;
  currentValue.splice(firstIndex, 2, currentValue[lastIndex], currentValue[firstIndex]);
};

const newOption = () => {
  const label = reactive({});
  props.enabledLocales.forEach((locale: Locale) => label[locale] = "");
  const newItem: SelectableOptionItem = {
    label: label,
    identifier: getRandomKey(),
  };
  currentValue.push(newItem);
};

const updateOption = (newValue: LocalString, identifier: string) =>
  currentValue.find(option => option.identifier === identifier).label = newValue;

const deleteOption = (index: number) => {
  const deleted = currentValue.splice(index, 1);
  deletedOptions.push(deleted[0].identifier);
};

const getRandomKey = () => (Math.random() + 1).toString(36).substring(2);

onMounted(() =>  {
  props.modelValue.forEach(option => currentValue.push(option));
});

watch(currentValue, () => {
  emits("update:modelValue", currentValue);
}, { deep: true });
</script>

<style>
.btn-delete-option {
  color: var(--bs-danger);
  border-left: var(--bs-border-color) var(--bs-border-width) solid;
}

.btn-delete-option:hover {
  color: var(--bs-white);
  background-color: var(--bs-danger);
}

.btn-new-option {
  background-color: var(--bs-light);
}

.btn-new-option:hover {
  color: var(--bs-white) !important;
  background-color: var(--bs-dark);
}
</style>
